import * as tus from 'tus-js-client';
import { supabase } from '@/lib/supabase';

const CHUNK_SIZE = 6 * 1024 * 1024; // 6MB - fixo do Supabase
const RETRY_DELAYS = [0, 3000, 5000, 10000, 20000];

export type ResumableUploadParams = {
  bucketName: string;
  storagePath: string;
  file: File;
  contentType?: string;
  onProgress?: (percentage: number, bytesUploaded: number, bytesTotal: number) => void;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
};

export type ResumableUploadHandle = {
  promise: Promise<void>;
  abort: () => void;
};

export async function resumableUpload({
  bucketName,
  storagePath,
  file,
  contentType,
  onProgress,
  onSuccess,
  onError,
}: ResumableUploadParams): Promise<ResumableUploadHandle> {
  // Obter token de autenticação
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();

  if (sessionError || !session) {
    const err = new Error('Usuário não autenticado');
    if (onError) onError(err);
    throw err;
  }

  // Construir endpoint TUS a partir da URL do Supabase
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const tusEndpoint = `${supabaseUrl}/storage/v1/upload/resumable`;

  let upload: tus.Upload | null = null;
  let rejectPromise: ((reason?: unknown) => void) | null = null;

  const promise = new Promise<void>((resolve, reject) => {
    rejectPromise = reject;
    upload = new tus.Upload(file, {
      endpoint: tusEndpoint,
      retryDelays: RETRY_DELAYS,
      chunkSize: CHUNK_SIZE,
      uploadDataDuringCreation: true,
      removeFingerprintOnSuccess: true,
      headers: {
        'x-upsert': 'true',
      },
      onBeforeRequest: async (req: any) => {
        const { data: { session: freshSession } } = await supabase.auth.getSession();
        if (freshSession) {
          req.setHeader('authorization', `Bearer ${freshSession.access_token}`);
        }
      },
      metadata: {
        bucketName: bucketName,
        objectName: storagePath,
        contentType: contentType || file.type || 'application/octet-stream',
        cacheControl: '3600',
      },
      onError: (error: Error) => {
        console.error('[resumableUpload] Erro:', error);
        if (onError) onError(error);
        reject(error);
      },
      onProgress: (bytesUploaded: number, bytesTotal: number) => {
        const percentage = parseFloat(((bytesUploaded / bytesTotal) * 100).toFixed(1));
        if (onProgress) onProgress(percentage, bytesUploaded, bytesTotal);
      },
      onSuccess: () => {
        if (onSuccess) onSuccess();
        resolve();
      },
    });

    upload.findPreviousUploads().then((previousUploads) => {
      if (!upload) return;
      if (previousUploads.length) {
        upload.resumeFromPreviousUpload(previousUploads[0]);
      }
      upload.start();
    });
  });

  return {
    promise,
    abort: () => {
      if (upload) {
        upload.abort(true);
        // tus.abort() não dispara onError — rejeitar manualmente para desbloquear o caller
        if (rejectPromise) rejectPromise(new Error('Upload cancelado'));
      }
    }
  };
}
